import { webScrollbarStyles } from "@/constants/scrollbar";
import { layout } from "@/constants";
import React from "react";
import {
  Platform,
  ScrollView as RNScrollView,
  ScrollViewProps as RNScrollViewProps,
  StyleSheet
} from "react-native";

/**
 * Custom ScrollView component with consistent scrollbar styling and optional default padding.
 * Automatically applies web-optimized scrollbar styles on web platform.
 *
 * @param props - Standard ScrollView props from React Native
 * @param props.contentContainerStyle - Optional style for the content container. Pass false to disable default padding.
 * @returns A ScrollView with platform-appropriate scrollbar styling
 *
 * @example
 * ```tsx
 * // With default padding (screen-level usage)
 * <ScrollView>
 *   <Text>Content here</Text>
 * </ScrollView>
 *
 * // Without default padding (component-level usage)
 * <ScrollView contentContainerStyle={false}>
 *   <Text>Custom styled content</Text>
 * </ScrollView>
 *
 * // With custom padding
 * <ScrollView contentContainerStyle={styles.custom}>
 *   <Text>Custom styled content</Text>
 * </ScrollView>
 * ```
 */
export default function ScrollView(props: RNScrollViewProps) {
  const {
    style,
    showsVerticalScrollIndicator,
    contentContainerStyle,
    ...rest
  } = props;

  // Apply default content padding unless explicitly disabled or overridden
  const finalContentContainerStyle =
    contentContainerStyle === false
      ? undefined
      : contentContainerStyle === undefined
        ? styles.defaultContent
        : [styles.defaultContent, contentContainerStyle];

  return (
    <RNScrollView
      style={[
        styles.scrollView,
        webScrollbarStyles as any,
        Platform.OS === "web" && { touchAction: "pan-y" as any },
        style
      ]}
      contentContainerStyle={finalContentContainerStyle}
      showsVerticalScrollIndicator={
        showsVerticalScrollIndicator !== undefined
          ? showsVerticalScrollIndicator
          : Platform.OS === "web"
      }
      {...rest}
    />
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1
  },
  defaultContent: {
    paddingHorizontal: layout.space.lg,
    paddingVertical: layout.space.lg,
    ...(Platform.OS === "web" && {
      paddingRight: layout.space.md // Extra right padding for web scrollbar
    })
  }
});
